import { useState } from "react";
import {
  Box,Typography,Button,TextField,FormControl,Select,MenuItem,Switch,Table,TableBody,
  TableCell,TableContainer,TableHead,TableRow,Checkbox,Dialog,DialogTitle,DialogContent,
  DialogActions
} from "@mui/material";
import { useAppTheme } from "../../../../context/ThemeContext";
import { useAlert } from "../../../../context/AlertContext";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

const emptyGroup={
  name:"",
  teams_advance:"",
  active:true
};

const getTeam=(item)=>item.team||item.tournament_team?.team||{};
const getTeamSlug=(item)=>getTeam(item).slug||item.slug||"";
const getTeamName=(item)=>getTeam(item).name||item.name||"";

export default function PhaseConfiguration({
  tournamentSlug,
  categorySlug,
  phase,
  groups=[],
  teams=[],
  onGroupsChange,
  onTeamsChange
}){
  const {colors,typography}=useAppTheme();
  const {showSuccess,showError}=useAlert();

  const [groupDialog,setGroupDialog]=useState(false);
  const [editingGroup,setEditingGroup]=useState(null);
  const [groupForm,setGroupForm]=useState(emptyGroup);
  const [savingGroup,setSavingGroup]=useState(false);
  const [savingTeams,setSavingTeams]=useState(false);

  const selectedCount=teams.filter((item)=>item.selected).length;
  const allSelected=teams.length>0&&teams.every((item)=>item.selected);

  const loadGroups=async()=>{
    if(phase.format!=="GROUPS") return [];

    const response=await fetch(
      `/tournaments/${tournamentSlug}/categories/${categorySlug}/phases/${phase.slug}/groups/`,
      {credentials:"include"}
    );

    const data=await readResponse(response);

    if(!response.ok){
      throw new Error(data.error||data.detail||`No se pudieron cargar los grupos de ${phase.name}.`);
    }

    return Array.isArray(data)?data:data.results||[];
  };

  const loadTeams=async()=>{
    const response=await fetch(
      `/tournaments/${tournamentSlug}/categories/${categorySlug}/phases/${phase.slug}/teams/`,
      {credentials:"include"}
    );

    const data=await readResponse(response);

    if(!response.ok){
      throw new Error(data.error||data.detail||`No se pudieron cargar los equipos de ${phase.name}.`);
    }

    const rows=Array.isArray(data)?data:data.results||[];

    return rows.map((item)=>({
      ...item,
      selected:Boolean(item.assigned??item.active),
      group:item.group?.slug||item.group||"",
      seed:item.seed??""
    }));
  };

  const reloadDetails=async()=>{
    try{
      const [phaseGroups,phaseTeams]=await Promise.all([
        phase.format==="GROUPS"?loadGroups():Promise.resolve([]),
        loadTeams()
      ]);

      onGroupsChange?.(phaseGroups);
      onTeamsChange?.(phaseTeams);
    }catch(error){
      showError(error.message||"No se pudo actualizar la configuración de la fase.");
    }
  };

  const openNewGroup=()=>{
    setEditingGroup(null);
    setGroupForm(emptyGroup);
    setGroupDialog(true);
  };

  const openEditGroup=(group)=>{
    setEditingGroup(group);
    setGroupForm({
      name:group.name||"",
      teams_advance:group.teams_advance??"",
      active:group.active!==false
    });
    setGroupDialog(true);
  };

  const closeGroupDialog=()=>{
    if(savingGroup) return;
    setGroupDialog(false);
    setEditingGroup(null);
    setGroupForm(emptyGroup);
  };

  const saveGroup=async()=>{
    if(!groupForm.name.trim()){
      showError("El nombre del grupo es obligatorio.");
      return;
    }

    const teamsAdvance=groupForm.teams_advance===""?null:Number(groupForm.teams_advance);

    if(teamsAdvance!==null&&(!Number.isInteger(teamsAdvance)||teamsAdvance<1)){
      showError("Los equipos que avanzan deben ser un número válido.");
      return;
    }

    try{
      setSavingGroup(true);

      const editing=Boolean(editingGroup?.slug);
      const url=editing
        ?`/tournaments/${tournamentSlug}/categories/${categorySlug}/phases/${phase.slug}/groups/${editingGroup.slug}/`
        :`/tournaments/${tournamentSlug}/categories/${categorySlug}/phases/${phase.slug}/groups/`;

      const response=await fetch(url,{
        method:editing?"PATCH":"POST",
        credentials:"include",
        headers:{
          "Content-Type":"application/json",
          "X-CSRFToken":getCookie("csrftoken")
        },
        body:JSON.stringify({
          name:groupForm.name.trim(),
          teams_advance:teamsAdvance,
          active:groupForm.active
        })
      });

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||data.detail||"No se pudo guardar el grupo.");
      }

      setGroupDialog(false);
      setEditingGroup(null);
      setGroupForm(emptyGroup);

      await reloadDetails();

      showSuccess(editing?"Grupo actualizado correctamente.":"Grupo creado correctamente.");
    }catch(error){
      showError(error.message||"No se pudo guardar el grupo.");
    }finally{
      setSavingGroup(false);
    }
  };

  const updateTeam=(teamSlug,changes)=>{
    onTeamsChange?.(
      teams.map((item)=>{
        if(getTeamSlug(item)!==teamSlug) return item;
        return {...item,...changes};
      })
    );
  };

  const toggleAllTeams=()=>{
    if(!teams.length) return;

    onTeamsChange?.(
      teams.map((item)=>({
        ...item,
        selected:!allSelected
      }))
    );
  };

  const assignAutomaticSeeds=()=>{
    if(!teams.some((item)=>item.selected)){
      showError("No hay equipos seleccionados.");
      return;
    }

    if(phase.format==="GROUPS"){
      const selected=teams.filter((item)=>item.selected);

      if(selected.some((item)=>!item.group)){
        showError("Primero asigna todos los equipos seleccionados a un grupo.");
        return;
      }

      const counters={};

      const updated=teams.map((item)=>{
        if(!item.selected) return item;

        counters[item.group]=(counters[item.group]||0)+1;

        return{
          ...item,
          seed:counters[item.group]
        };
      });

      onTeamsChange?.(updated);
      showSuccess("Posiciones asignadas automáticamente.");
      return;
    }

    let position=0;

    const updated=teams.map((item)=>{
      if(!item.selected) return item;

      position+=1;

      return{
        ...item,
        seed:position
      };
    });

    onTeamsChange?.(updated);
    showSuccess("Posiciones asignadas automáticamente.");
  };

  const distributeAutomatically=()=>{
    if(phase.format!=="GROUPS") return;

    const activeGroups=[...groups]
      .filter((group)=>group.active)
      .sort((a,b)=>(a.order??0)-(b.order??0));

    if(activeGroups.length===0){
      showError("Primero debes crear al menos un grupo activo.");
      return;
    }

    const selectedRows=teams.filter((item)=>item.selected);

    if(selectedRows.length===0){
      showError("No hay equipos seleccionados.");
      return;
    }

    const sortedSelected=[...selectedRows].sort((a,b)=>
      getTeamName(a).localeCompare(getTeamName(b),"es",{sensitivity:"base"})
    );

    const assignments={};

    sortedSelected.forEach((item,index)=>{
      const group=activeGroups[index%activeGroups.length];
      const teamSlug=getTeamSlug(item);
      const groupPosition=Math.floor(index/activeGroups.length)+1;

      assignments[teamSlug]={
        group:group.slug,
        seed:groupPosition
      };
    });

    const updated=teams.map((item)=>{
      const assignment=assignments[getTeamSlug(item)];
      if(!assignment) return item;

      return{
        ...item,
        group:assignment.group,
        seed:assignment.seed
      };
    });

    onTeamsChange?.(updated);
    showSuccess("Equipos distribuidos automáticamente entre los grupos.");
  };

  const saveTeams=async()=>{
    const selected=teams.filter((item)=>item.selected);

    if(!selected.length){
      showError("Selecciona al menos un equipo para la fase.");
      return;
    }

    if(phase.format==="GROUPS"&&selected.some((item)=>!item.group)){
      showError("Todos los equipos seleccionados deben tener un grupo.");
      return;
    }

    const invalidSeed=selected.some((item)=>{
      if(item.seed==="") return false;
      const value=Number(item.seed);
      return !Number.isInteger(value)||value<1;
    });

    if(invalidSeed){
      showError("Las posiciones deben ser números enteros mayores a cero.");
      return;
    }

    try{
      setSavingTeams(true);

      const payloadTeams=selected.map((item)=>({
        team:getTeamSlug(item),
        group:phase.format==="GROUPS"?item.group:null,
        seed:item.seed===""?null:Number(item.seed)
      }));

      const response=await fetch(
        `/tournaments/${tournamentSlug}/categories/${categorySlug}/phases/${phase.slug}/teams/`,
        {
          method:"POST",
          credentials:"include",
          headers:{
            "Content-Type":"application/json",
            "X-CSRFToken":getCookie("csrftoken")
          },
          body:JSON.stringify({teams:payloadTeams})
        }
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||data.detail||"No se pudieron guardar los equipos de la fase.");
      }

      await reloadDetails();
      showSuccess("Equipos de la fase actualizados correctamente.");
    }catch(error){
      showError(error.message||"No se pudieron guardar los equipos de la fase.");
    }finally{
      setSavingTeams(false);
    }
  };

  return(
    <>
      {phase.format==="GROUPS"&&(
        <Box sx={{mt:1.2}}>
          <Box sx={{mb:0.8,display:"flex",alignItems:"center",justifyContent:"space-between",gap:1}}>
            <Typography sx={{fontSize:typography.sizes.md,fontWeight:700,color:colors.text}}>
              Grupos
            </Typography>

            <Button
              size="small"
              onClick={openNewGroup}
              sx={{
                textTransform:"none",
                color:colors.secondary
              }}
            >
              Nuevo grupo
            </Button>
          </Box>

          <TableContainer
            sx={{
              border:`1px solid ${colors.border}`,
              borderRadius:"10px",
              backgroundColor:colors.surface,
              boxShadow:"none",
              overflow:"hidden"
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{backgroundColor:colors.tableHeader}}>
                  <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>
                    Grupo
                  </TableCell>

                  <TableCell align="center" sx={{color:colors.tableHeaderText,fontWeight:700}}>
                    Equipos que avanzan
                  </TableCell>

                  <TableCell align="center" sx={{color:colors.tableHeaderText,fontWeight:700}}>
                    Activo
                  </TableCell>

                  <TableCell align="right" sx={{color:colors.tableHeaderText,fontWeight:700}}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {groups.length===0?(
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{py:3,color:colors.textSecondary}}>
                      Esta fase todavía no tiene grupos.
                    </TableCell>
                  </TableRow>
                ):(
                  groups.map((group)=>(
                    <TableRow key={group.slug} hover>
                      <TableCell sx={{fontWeight:600,color:colors.text}}>
                        {group.name}
                      </TableCell>

                      <TableCell align="center" sx={{color:colors.text}}>
                        {group.teams_advance??"—"}
                      </TableCell>

                      <TableCell align="center" sx={{color:colors.text}}>
                        {group.active?"Sí":"No"}
                      </TableCell>

                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={()=>openEditGroup(group)}
                          sx={{
                            minWidth:0,
                            textTransform:"none",
                            color:colors.secondary
                          }}
                        >
                          Editar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Box sx={{mt:1.2}}>
        <Box
          sx={{
            mb:0.8,
            display:"flex",
            flexDirection:{xs:"column",sm:"row"},
            alignItems:{xs:"stretch",sm:"center"},
            justifyContent:"space-between",
            gap:1
          }}
        >
          <Box>
            <Typography sx={{fontSize:typography.sizes.md,fontWeight:700,color:colors.text}}>
              Equipos de la fase
            </Typography>

            <Typography sx={{mt:0.2,fontSize:typography.sizes.sm,color:colors.textSecondary}}>
              {selectedCount} seleccionado{selectedCount===1?"":"s"}
            </Typography>
          </Box>

          <Box
            sx={{
              display:"flex",
              flexWrap:"wrap",
              justifyContent:{xs:"flex-start",sm:"flex-end"},
              gap:0.5
            }}
          >
            {phase.format==="GROUPS"&&(
              <Button
                size="small"
                disabled={selectedCount===0||groups.filter((group)=>group.active).length===0}
                onClick={distributeAutomatically}
                sx={{
                  textTransform:"none",
                  color:colors.secondary
                }}
              >
                Distribuir automáticamente
              </Button>
            )}

            <Button
              size="small"
              disabled={selectedCount===0}
              onClick={assignAutomaticSeeds}
              sx={{
                textTransform:"none",
                color:colors.secondary
              }}
            >
              Asignar posiciones
            </Button>
          </Box>
        </Box>

        <TableContainer
          sx={{
            border:`1px solid ${colors.border}`,
            borderRadius:"10px",
            backgroundColor:colors.surface,
            boxShadow:"none",
            overflowX:"auto"
          }}
        >
          <Table size="small" sx={{minWidth:phase.format==="GROUPS"?650:450}}>
            <TableHead>
              <TableRow sx={{backgroundColor:colors.tableHeader}}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={selectedCount>0&&!allSelected}
                    disabled={teams.length===0}
                    onChange={toggleAllTeams}
                    sx={{
                      color:colors.tableHeaderText,
                      "&.Mui-checked":{color:colors.tableHeaderText},
                      "&.MuiCheckbox-indeterminate":{color:colors.tableHeaderText}
                    }}
                  />
                </TableCell>

                <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>
                  Equipo
                </TableCell>

                {phase.format==="GROUPS"&&(
                  <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>
                    Grupo
                  </TableCell>
                )}

                <TableCell align="center" sx={{color:colors.tableHeaderText,fontWeight:700}}>
                  Posición
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {teams.length===0?(
                <TableRow>
                  <TableCell
                    colSpan={phase.format==="GROUPS"?4:3}
                    align="center"
                    sx={{py:4,color:colors.textSecondary}}
                  >
                    No hay equipos disponibles para esta fase.
                  </TableCell>
                </TableRow>
              ):(
                teams.map((item)=>{
                  const team=getTeam(item);
                  const teamSlug=getTeamSlug(item);

                  return(
                    <TableRow key={teamSlug} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={item.selected}
                          onChange={()=>updateTeam(teamSlug,{selected:!item.selected})}
                        />
                      </TableCell>

                      <TableCell>
                        <Box sx={{display:"flex",alignItems:"center",gap:1}}>
                          {team.logo&&(
                            <Box
                              component="img"
                              src={team.logo}
                              alt={team.name}
                              sx={{
                                width:32,
                                height:32,
                                objectFit:"contain",
                                flexShrink:0
                              }}
                            />
                          )}

                          <Typography
                            sx={{
                              fontSize:typography.sizes.md,
                              fontWeight:600,
                              color:colors.text
                            }}
                          >
                            {team.name||item.name}
                          </Typography>
                        </Box>
                      </TableCell>

                      {phase.format==="GROUPS"&&(
                        <TableCell sx={{width:220}}>
                          <FormControl size="small" fullWidth disabled={!item.selected}>
                            <Select
                              value={item.group||""}
                              displayEmpty
                              onChange={(event)=>
                                updateTeam(teamSlug,{
                                  group:event.target.value,
                                  seed:""
                                })
                              }
                            >
                              <MenuItem value="">
                                Seleccionar grupo
                              </MenuItem>

                              {groups
                                .filter((group)=>group.active)
                                .map((group)=>(
                                  <MenuItem key={group.slug} value={group.slug}>
                                    {group.name}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      )}

                      <TableCell align="center" sx={{width:130}}>
                        <TextField
                          size="small"
                          type="number"
                          value={item.seed}
                          disabled={!item.selected}
                          onChange={(event)=>updateTeam(teamSlug,{seed:event.target.value})}
                          inputProps={{min:1}}
                          sx={{width:80}}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {teams.length>0&&(
          <Box sx={{mt:1,display:"flex",justifyContent:"flex-end"}}>
            <Button
              size="small"
              variant="contained"
              disabled={savingTeams}
              onClick={saveTeams}
              sx={{
                px:2,
                textTransform:"none",
                backgroundColor:colors.secondary,
                "&:hover":{backgroundColor:colors.secondaryHover}
              }}
            >
              {savingTeams?"Guardando...":"Guardar equipos"}
            </Button>
          </Box>
        )}
      </Box>

      <Dialog
        open={groupDialog}
        onClose={closeGroupDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Typography
            sx={{
              fontSize:typography.sizes.xl,
              fontWeight:700,
              color:colors.text
            }}
          >
            {editingGroup?"Editar grupo":"Nuevo grupo"}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              pt:1,
              display:"flex",
              flexDirection:"column",
              gap:1.5
            }}
          >
            <TextField
              size="small"
              label="Nombre del grupo"
              value={groupForm.name}
              onChange={(event)=>
                setGroupForm((current)=>({
                  ...current,
                  name:event.target.value
                }))
              }
              fullWidth
              autoFocus
            />

            <TextField
              size="small"
              label="Equipos que avanzan"
              type="number"
              value={groupForm.teams_advance}
              onChange={(event)=>
                setGroupForm((current)=>({
                  ...current,
                  teams_advance:event.target.value
                }))
              }
              inputProps={{min:1}}
              fullWidth
            />

            <Box
              sx={{
                display:"flex",
                alignItems:"center",
                justifyContent:"space-between",
                border:`1px solid ${colors.border}`,
                borderRadius:"8px",
                px:1.5,
                py:0.7
              }}
            >
              <Typography sx={{fontSize:typography.sizes.md,color:colors.text}}>
                Grupo activo
              </Typography>

              <Switch
                size="small"
                checked={groupForm.active}
                onChange={(event)=>
                  setGroupForm((current)=>({
                    ...current,
                    active:event.target.checked
                  }))
                }
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            size="small"
            disabled={savingGroup}
            onClick={closeGroupDialog}
            sx={{
              textTransform:"none",
              color:colors.textSecondary
            }}
          >
            Cancelar
          </Button>

          <Button
            size="small"
            variant="contained"
            disabled={savingGroup}
            onClick={saveGroup}
            sx={{
              textTransform:"none",
              backgroundColor:colors.secondary,
              "&:hover":{backgroundColor:colors.secondaryHover}
            }}
          >
            {savingGroup?"Guardando...":"Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}